import { useScopedRestaurant } from '../../../../../hooks/restaurantViewScope';
import { recipeWriteVersion, StaleRecipeWriteError } from '../../../../../services/recipeWriteConflict';
import { usePracticeDataset } from '../../../../onboarding/practice/practiceDataset';
import { usePracticeScope } from '../../../../onboarding/practice/practiceScope';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLoadOrderHistory, useLoadSubrecipes } from "../../../../../utils/dataLoadingUtils";
import { useOrderHistory, adjustProductPriceForQuantityInfo } from "../../../../../stores/appOrderHistoryStore";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import { useAppUserDefaultStore } from "../../../../../stores/appUserDefaultStore";
import { useRestaurantStore } from "../../../../../stores/appRestaurantStore";
import {
    Subrecipe,
    SubrecipeIngredient,
    SubrecipeIngredientInput,
    createSubrecipe,
    updateSubrecipe,
    fetchSubrecipeById,
    fetchSubrecipesByRestaurant,
    SubrecipeCycleApiError,
    SubrecipeDepthApiError,
    SubrecipeDuplicateNameApiError,
} from "../../../../../services/subrecipes/subrecipesService";
import { CustomIngredient, fetchCustomIngredientsByRestaurant } from "../../../../../services/customIngredients/customIngredientsService";
import { CostPriceIngredient } from "../../../../../services/costPrice/costPriceService";
import { calculateCurrentPrice, calculateSubrecipeTotalCost, calculateSubrecipeUnitCost } from "../../../../../utils/costPriceUtils";
import { computeSubrecipeAncestors } from "../../../../../utils/subrecipeUtils";
import { resolveWastePercentage } from "../../../../../utils/wasteUtils";
import { productKey } from "../../../../../utils/reports/shared";
import { linkedSubrecipeRow } from "../../review/customLinkAdapter";
import type { SwapCandidate } from "../../review/reviewAdapter";
import type { AddIngredientSelection } from "./AddIngredients";

import { fetchOrderHistoryByRestaurant } from "../../../../../services/orderhistory/orderhistoryService";
import { SourceRestaurantData, useSourceRestaurantDataStore } from "../../../../../stores/appSourceRestaurantDataStore";

const tempId = () => `temp-${Date.now()}-${Math.random()}`;

/** An ingredient row a host wants the draft to start with (see `seedIngredient`). */
export type SubrecipeSeedIngredient = Omit<SubrecipeIngredient, "id">;

export interface UseSubrecipeDetailOptions {
    /**
     * Row to pre-add to the draft — the product-setup flow seeds the product being
     * set up so the user only has to type its amount. Create mode starts with it;
     * edit mode appends it right after the fetch resolves (never on a failed load,
     * which would save an emptied ingredient list). Applied at most once.
     */
    seedIngredient?: SubrecipeSeedIngredient;
    allowEmptyIngredients?: boolean;
    seedName?: string;
    /** Load a sibling editor locally without changing the selected restaurant stores. */
    restaurantId?: string;
    /**
     * Whether a successful save closes the panel (default true). The setup flow
     * passes false: `onSaved` advances its stepper instead.
     */
    closeOnSave?: boolean;
}

/**
 * True when `seed` is already on `rows` — as a normal ingredient OR as any row's
 * alternative. Mirrors the cost-price setup path's guard (ProductSetupModal), so a
 * product that is already covered is never added a second time.
 */
function seedAlreadyPresent(rows: SubrecipeIngredient[], seed: SubrecipeSeedIngredient): boolean {
    // A nested-subrecipe seed carries no product identity — it matches on the reference id.
    if (seed.nestedSubrecipeId) return rows.some(row => row.nestedSubrecipeId === seed.nestedSubrecipeId);
    const target = productKey(seed.platform ?? "", seed.articleId, seed.productName);
    return rows.some(row => {
        const isNormal = !row.nestedSubrecipeId && !row.isCustom;
        if (isNormal && productKey(row.platform ?? "", row.articleId, row.productName) === target) return true;
        return (row.alternatives ?? []).some(alt => productKey(alt.platform, alt.articleId, alt.productName) === target);
    });
}

// Strip the local `id` and normalize a draft row into the create/update payload
// shape. Nested-reference rows carry only the reference fields.
export function toIngredientInput(ing: SubrecipeIngredient): SubrecipeIngredientInput {
    if (ing.nestedSubrecipeId) {
        return {
            nestedSubrecipeId: ing.nestedSubrecipeId,
            nestedSubrecipePortionsNeeded: ing.nestedSubrecipePortionsNeeded ?? 0,
        };
    }
    return {
        articleId: ing.articleId,
        productName: ing.productName,
        quantityNeeded: ing.quantityNeeded,
        platform: ing.platform,
        customPlatformName: ing.customPlatformName,
        quantityInfo: ing.quantityInfo,
        isCustom: ing.isCustom,
        wastePercentage: ing.wastePercentage,
        // Alternatives ride along on normal rows only; the server strips them from
        // custom rows on write. Undefined for custom ingredients (never offered there).
        alternatives: ing.isCustom ? undefined : ing.alternatives,
    };
}

function draftSignature(value: Pick<Subrecipe, 'name' | 'description' | 'portions' | 'portionsUnit' | 'ingredients'>): string {
    return JSON.stringify({ name: value.name.trim(), description: value.description?.trim() ?? '', portions: value.portions, portionsUnit: value.portionsUnit, ingredients: value.ingredients.map(toIngredientInput) });
}

/**
 * Local-draft controller for the subrecipe side modal. Mirrors `useCostPriceDetail`
 * but holds the whole subrecipe (fields + ingredient rows) in local state and
 * persists it in ONE createSubrecipe/updateSubrecipe call on Save. Cycle/depth
 * errors from the service are surfaced as translated messages.
 *
 * `subrecipeId` null → create mode (empty draft); a string → edit mode.
 */
export function useSubrecipeDetail(
    subrecipeId: string | null,
    onClose: () => void,
    // "something changed, refresh": called with the saved subrecipe after a
    // create/update, and with nothing after a delete (see SubrecipeDetailBody).
    onSaved?: (saved?: Subrecipe) => void,
    options?: UseSubrecipeDetailOptions,
) {
    const { t } = useTranslation();
    const isEditMode = subrecipeId !== null;
    const closeOnSave = options?.closeOnSave ?? true;
    // Applied-once guard for `seedIngredient` (survives StrictMode's double-invoke and
    // an inline-built seed object whose identity changes every render).
    const seededRef = useRef(false);

    // --- Data loading ---
    useLoadOrderHistory();
    useLoadSubrecipes();

    const practiceScope = usePracticeScope();
    const practiceData = usePracticeDataset(practiceScope);
    const liveOrders = useOrderHistory(s => s.orderHistoryList);
    const liveSubrecipesById = useSubrecipesStore(s => s.subrecipesById);
    const liveSubrecipesList = useSubrecipesStore(s => s.subrecipesList);
    const reloadLiveSubrecipes = useSubrecipesStore(s => s.reload);
    const restaurant = useScopedRestaurant();
    const restaurantId = practiceScope ? restaurant?.id : options?.restaurantId ?? restaurant?.id;
    const sourceContext = !practiceScope && !!options?.restaurantId && options.restaurantId !== restaurant?.id;
    const [sourceData, setSourceData] = useState<{ restaurantId: string; data: SourceRestaurantData }>();
    const [sourceLoading, setSourceLoading] = useState(sourceContext);
    const [sourceFailed, setSourceFailed] = useState(false);
    const scopedData = sourceContext && sourceData && sourceData.restaurantId === restaurantId ? sourceData.data : undefined;
    const emptySubrecipes = useMemo(() => new Map<string, Subrecipe>(), []);
    const emptySourceData = useMemo<SourceRestaurantData>(() => ({ orders: [], customIngredients: [], subrecipes: emptySubrecipes }), [emptySubrecipes]);
    const orders = useMemo(() => practiceData?.orderHistoryList ?? (sourceContext ? scopedData?.orders ?? [] : liveOrders), [practiceData, sourceContext, scopedData, liveOrders]);
    const subrecipesById = practiceData?.subrecipesById ?? (sourceContext ? scopedData?.subrecipes ?? emptySubrecipes : liveSubrecipesById);
    const subrecipesList = useMemo(() => practiceData?.subrecipesList ?? (sourceContext ? Array.from(subrecipesById.values()) : liveSubrecipesList), [practiceData, sourceContext, subrecipesById, liveSubrecipesList]);
    const loadSourceData = useCallback(async () => {
        if (!restaurantId) return undefined;
        const [rawOrders, customs, recipes] = await Promise.all([
            fetchOrderHistoryByRestaurant(restaurantId), fetchCustomIngredientsByRestaurant(restaurantId), fetchSubrecipesByRestaurant(restaurantId),
        ]);
        return { restaurantId, data: {
            orders: rawOrders.map(order => ({ ...order, productData: order.productData.map(product => adjustProductPriceForQuantityInfo(product, order.platform)) })),
            customIngredients: customs, subrecipes: new Map(recipes.map(recipe => [recipe.id, recipe])),
        } };
    }, [restaurantId]);
    const reloadSubrecipes = useCallback(async () => {
        if (sourceContext) {
            const loaded = await loadSourceData();
            setSourceData(loaded);
            if (loaded) useSourceRestaurantDataStore.setState(state => ({
                dataByRestaurant: { ...state.dataByRestaurant, [loaded.restaurantId]: loaded.data },
                errorByRestaurant: { ...state.errorByRestaurant, [loaded.restaurantId]: false },
            }));
        } else await reloadLiveSubrecipes();
    }, [sourceContext, loadSourceData, reloadLiveSubrecipes]);
    const showExVat = useAppUserDefaultStore(s => s.showExVatOrders);
    const reportSettingsMap = useRestaurantStore(s => s.reportSettings);
    const defaultWastePercentage = !practiceData && restaurantId
        ? resolveWastePercentage(reportSettingsMap[restaurantId]?.wastePercentage)
        : 0;

    const [liveCustomIngredients, setCustomIngredients] = useState<CustomIngredient[]>([]);
    const customIngredients = useMemo(() => practiceData?.customIngredients ?? (sourceContext ? scopedData?.customIngredients ?? [] : liveCustomIngredients), [practiceData, sourceContext, scopedData, liveCustomIngredients]);

    // --- Draft state ---
    const [name, setName] = useState(() => isEditMode ? "" : options?.seedName ?? "");
    const [description, setDescription] = useState("");
    const [portions, setPortions] = useState<number>(1);
    const [portionsInputValue, setPortionsInputValue] = useState<string>("1");
    const [portionsUnit, setPortionsUnit] = useState<string>("p");
    const [ingredients, setIngredients] = useState<SubrecipeIngredient[]>([]);
    // This token belongs to the loaded draft, never to a later store refresh.
    const [draftVersion, setDraftVersion] = useState<string>();
    const [savedDraftSignature, setSavedDraftSignature] = useState<string>();

    // --- Inline edit buffers (raw strings while typing) ---
    const [inlineQuantities, setInlineQuantities] = useState<Record<string, string>>({});
    const [inlineWaste, setInlineWaste] = useState<Record<string, string>>({});
    const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);

    // --- UI state ---
    const [isPanelVisible, setIsPanelVisible] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [showAddIngredients, setShowAddIngredients] = useState(false);
    const [selectedNestedSubrecipeId, setSelectedNestedSubrecipeId] = useState<string | null>(null);
    // Draft-row id whose Alternatives editor is open (view-swap). Rows are stable
    // temp ids within the modal session, so keying by id is safe here.
    const [alternativesIngredientId, setAlternativesIngredientId] = useState<string | null>(null);
    // Draft-row id whose custom-ingredient link picker is open.
    const [customLinkIngredientId, setCustomLinkIngredientId] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const [isLoading, setIsLoading] = useState(isEditMode);
    // Create mode has a usable draft immediately; edit mode only once the fetch lands.
    const [isDraftReady, setIsDraftReady] = useState(!isEditMode);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // --- Slide-in animation ---
    useEffect(() => {
        document.body.style.overflow = "hidden";
        setIsPanelVisible(true);
        return () => { document.body.style.overflow = ""; };
    }, []);

    useEffect(() => {
        if (!sourceContext) return;
        let current = true;
        setSourceLoading(true); setSourceFailed(false);
        void loadSourceData().then(data => { if (current) setSourceData(data); }).catch(error => {
            console.error("Failed to load source subrecipe data:", error);
            if (current) setSourceFailed(true);
        }).finally(() => { if (current) setSourceLoading(false); });
        return () => { current = false; };
    }, [sourceContext, loadSourceData]);

    // --- Load current restaurant's custom ingredients (needed for live cost) ---
    useEffect(() => {
        if (practiceData || sourceContext || !restaurantId) return;
        fetchCustomIngredientsByRestaurant(restaurantId)
            .then(setCustomIngredients)
            .catch(err => console.error("Failed to load custom ingredients:", err));
    }, [practiceData, sourceContext, restaurantId]);

    // --- Seed the draft with the host's row, once ---
    // Gated on `isDraftReady`: create mode is ready immediately, edit mode only after a
    // SUCCESSFUL load. Seeding onto the empty list of a failed load would save away
    // every existing ingredient. Re-runs if the seed arrives late; the ref keeps it once.
    useEffect(() => {
        const seed = options?.seedIngredient;
        if (!seed || seededRef.current || !isDraftReady) return;
        seededRef.current = true;
        setIngredients(prev => (seedAlreadyPresent(prev, seed) ? prev : [...prev, { ...seed, id: tempId() }]));
    }, [isDraftReady, options?.seedIngredient]);

    // --- Load the subrecipe being edited ---
    useEffect(() => {
        if (!subrecipeId) return;
        let cancelled = false;
        setIsLoading(true);
        fetchSubrecipeById(subrecipeId)
            .then(sub => {
                if (cancelled) return;
                setName(sub.name);
                setDescription(sub.description ?? "");
                setPortions(sub.portions);
                setPortionsInputValue(sub.portions.toString());
                setPortionsUnit(sub.portionsUnit || "p");
                setIngredients(sub.ingredients);
                setDraftVersion(recipeWriteVersion(sub));
                setSavedDraftSignature(draftSignature(sub));
                setIsDraftReady(true); // releases the seeding effect (success only)
                setIsLoading(false);
            })
            .catch(err => {
                if (cancelled) return;
                console.error("Failed to load subrecipe:", err);
                setError(t("costPrice.editSubrecipe.failedToUpdate"));
                setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, [subrecipeId, t]);

    // --- Auto-revert delete confirmation ---
    useEffect(() => {
        if (!confirmingDeleteId) return;
        const timer = setTimeout(() => setConfirmingDeleteId(null), 2000);
        return () => clearTimeout(timer);
    }, [confirmingDeleteId]);

    // --- Portions input (numeric-guarded) ---
    const handlePortionsChange = useCallback((value: string) => {
        if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
        setPortionsInputValue(value);
        setPortions(value === "" ? 0 : parseFloat(value) || 0);
    }, []);

    // --- Split draft rows: normal ingredients vs nested-subrecipe references ---
    const normalRows = useMemo(() => ingredients.filter(i => !i.nestedSubrecipeId), [ingredients]);
    const nestedRefRows = useMemo(() => ingredients.filter(i => !!i.nestedSubrecipeId), [ingredients]);

    // Enrich normal rows with a live per-std-unit price so the ingredient cards
    // (reused from IngredientsSection) can show unit price + row cost.
    const enrichedNormalIngredients = useMemo(() => {
        return normalRows.map(ing => {
            const cpShape = {
                id: ing.id,
                articleId: ing.articleId ?? "",
                productName: ing.productName ?? "",
                quantityNeeded: ing.quantityNeeded ?? 0,
                quantityInfo: ing.quantityInfo ?? { stdAmount: 0, stdMultiplier: 0, stdUnit: "" },
                platform: ing.platform ?? "",
                customPlatformName: ing.customPlatformName,
                custom: ing.isCustom,
            } as CostPriceIngredient;
            const { currentPrice } = calculateCurrentPrice(cpShape, orders, customIngredients, showExVat);
            return { ...ing, currentPrice };
        });
    }, [normalRows, orders, customIngredients, showExVat]);

    // Nested rows resolved to display data (child name/yield + live cost).
    const nestedRows = useMemo(() => {
        return nestedRefRows.map(ref => {
            const child = subrecipesById.get(ref.nestedSubrecipeId!);
            const portionsNeeded = ref.nestedSubrecipePortionsNeeded ?? 0;
            const unitCost = child
                ? calculateSubrecipeUnitCost(child, orders, customIngredients, showExVat, subrecipesById, defaultWastePercentage)
                : 0;
            return {
                id: ref.id,
                nestedSubrecipeId: ref.nestedSubrecipeId!,
                name: child?.name ?? t("costPrice.subrecipes.deletedSubrecipeLabel"),
                isOrphan: !child,
                portionsNeeded,
                portionsUnit: child?.portionsUnit ?? "p",
                cost: unitCost * portionsNeeded,
            };
        });
    }, [nestedRefRows, subrecipesById, orders, customIngredients, showExVat, defaultWastePercentage, t]);

    // --- Live cost footer: total cost + cost per portion ---
    const draftSubrecipe = useMemo<Subrecipe>(() => ({
        id: subrecipeId ?? "draft",
        restaurantId: restaurantId ?? "",
        name,
        description,
        portions,
        portionsUnit,
        ingredients,
        createdAt: new Date(),
        updatedAt: new Date(),
    }), [subrecipeId, restaurantId, name, description, portions, portionsUnit, ingredients]);

    const totalCost = useMemo(
        () => calculateSubrecipeTotalCost(draftSubrecipe, orders, customIngredients, showExVat, subrecipesById, defaultWastePercentage),
        [draftSubrecipe, orders, customIngredients, showExVat, subrecipesById, defaultWastePercentage]
    );
    const unitCost = useMemo(
        () => calculateSubrecipeUnitCost(draftSubrecipe, orders, customIngredients, showExVat, subrecipesById, defaultWastePercentage),
        [draftSubrecipe, orders, customIngredients, showExVat, subrecipesById, defaultWastePercentage]
    );

    // --- Save gating ---
    // Every nested-subrecipe reference must carry a positive portions-needed
    // (empty input coerces to 0, which would silently cost €0). Normal-row
    // quantities can't reach 0 — handleSaveEditQuantity rejects <= 0.
    const allNestedPortionsValid = nestedRefRows.every(r => (r.nestedSubrecipePortionsNeeded ?? 0) > 0);
    const canSave = (!sourceContext || (!sourceLoading && !sourceFailed && !!scopedData)) && name.trim() !== "" && (ingredients.length > 0 || !!options?.allowEmptyIngredients) && portions > 0 && allNestedPortionsValid && !isSaving;

    // --- Append a picked ingredient/subrecipe to the draft ---
    const handleAddSelection = useCallback((selection: AddIngredientSelection) => {
        setIngredients(prev => {
            switch (selection.kind) {
                case "product": {
                    const p = selection.product;
                    return [...prev, {
                        id: tempId(),
                        articleId: p.articleId,
                        productName: p.name,
                        quantityNeeded: selection.quantityNeeded,
                        platform: p.platform,
                        quantityInfo: p.quantityInfo ?? { stdAmount: 1, stdMultiplier: 1, stdUnit: "piece" },
                        isCustom: false,
                    }];
                }
                case "custom": {
                    const c = selection.customIngredient;
                    return [...prev, {
                        id: tempId(),
                        articleId: c.articleId,
                        productName: c.productName,
                        quantityNeeded: selection.quantityNeeded,
                        platform: c.platform,
                        customPlatformName: c.customPlatformName,
                        quantityInfo: c.quantityInfo,
                        isCustom: true,
                    }];
                }
                case "subrecipe": {
                    return [...prev, {
                        id: tempId(),
                        nestedSubrecipeId: selection.subrecipe.id,
                        nestedSubrecipePortionsNeeded: selection.quantityNeeded,
                    }];
                }
                // costPriceItem (nested cost prices) is cost-price-only; the picker
                // hides that tab in subrecipe mode, so it never reaches here.
                default:
                    return prev;
            }
        });
    }, []);

    // --- Normal-row inline edits (committed to the draft) ---
    const handleQuantityChange = useCallback((id: string, value: string) => {
        setInlineQuantities(prev => ({ ...prev, [id]: value }));
    }, []);

    const handleSaveEditQuantity = useCallback((id: string, qtyStr: string) => {
        const qty = parseFloat(qtyStr);
        if (isNaN(qty) || qty <= 0) return;
        setIngredients(prev => prev.map(i => (i.id === id ? { ...i, quantityNeeded: qty } : i)));
    }, []);

    const handleWasteChange = useCallback((id: string, value: string) => {
        setInlineWaste(prev => ({ ...prev, [id]: value }));
    }, []);

    const handleSaveWaste = useCallback((id: string, value: string) => {
        const trimmed = value.trim();
        let wastePercentage: number | undefined;
        if (trimmed === "") {
            wastePercentage = undefined; // inherit restaurant default
        } else {
            const parsed = parseFloat(trimmed);
            if (isNaN(parsed) || parsed < 0 || parsed > 100) return;
            wastePercentage = parsed;
        }
        setIngredients(prev => prev.map(i => (i.id === id ? { ...i, wastePercentage } : i)));
    }, []);

    // --- Alternatives (draft-local; persisted with the whole ingredient array) ---
    const handleSaveAlternatives = useCallback((id: string, alternatives: SubrecipeIngredient["alternatives"]) => {
        setIngredients(prev => prev.map(i => (i.id === id ? { ...i, alternatives } : i)));
    }, []);

    /**
     * Re-point a CUSTOM draft row at a real order-history product (or a subrecipe).
     * Draft-local, like every other edit in this editor: the whole ingredient array is
     * persisted in one updateSubrecipe on Save. Referencing dishes are deliberately NOT
     * rewritten (spec 1 §3 / README trap 4) — a dish's subrecipe row is a reference that
     * the enrich pass resolves live, so it picks the change up on its own. The
     * `custom_ingredients` row itself is never touched (deleting it would
     * cascade-delete its inventory assignments).
     */
    const handleApplyCustomLink = useCallback(async (id: string, candidate: SwapCandidate, quantity: number) => {
        setIngredients(prev => prev.map(row => (row.id === id ? linkedSubrecipeRow(row, candidate, quantity) : row)));
        // Drop the row's inline quantity buffer — it still holds the pre-link amount,
        // which is now expressed in a different product's unit.
        setInlineQuantities(prev => {
            if (prev[id] === undefined) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    const handleDeleteRow = useCallback((ingredient: { id: string }) => {
        setConfirmingDeleteId(ingredient.id);
    }, []);

    const handleConfirmDeleteRow = useCallback((ingredient: { id: string }) => {
        setIngredients(prev => prev.filter(i => i.id !== ingredient.id));
        setConfirmingDeleteId(null);
    }, []);

    // --- Nested-row edits ---
    const handleNestedQuantityChange = useCallback((id: string, value: string) => {
        setInlineQuantities(prev => ({ ...prev, [id]: value }));
        if (value === "" || /^\d*\.?\d*$/.test(value)) {
            const qty = value === "" ? 0 : parseFloat(value) || 0;
            setIngredients(prev => prev.map(i => (i.id === id ? { ...i, nestedSubrecipePortionsNeeded: qty } : i)));
        }
    }, []);

    // Subrecipes to hide from the picker: the edited subrecipe itself + every
    // subrecipe whose tree already contains it (nesting one would cycle). Empty
    // in create mode (the draft has no id yet).
    const excludedSubrecipeIds = useMemo(() => {
        if (!subrecipeId) return new Set<string>();
        const ancestors = computeSubrecipeAncestors(Array.from(subrecipesById.values()), subrecipeId);
        ancestors.add(subrecipeId);
        return ancestors;
    }, [subrecipeId, subrecipesById]);

    // --- Close (slide-out then unmount) ---
    const handleClose = useCallback(() => {
        setIsPanelVisible(false);
        setTimeout(onClose, 200);
    }, [onClose]);

    // --- Save (create or update in one call) ---
    const handleSave = useCallback(async () => {
        if (!restaurantId || !canSave) return;

        // Client-side duplicate-name guard. The subrecipes table has
        // UNIQUE(restaurant_id, name), but the server currently surfaces the
        // violation as a generic 500 — catch the common case here with an
        // actionable message before the round-trip (server 500 stays as backstop).
        const trimmedName = name.trim();
        const isDuplicateName = subrecipesList.some(
            s => s.id !== subrecipeId && s.name.trim().toLowerCase() === trimmedName.toLowerCase()
        );
        if (isDuplicateName) {
            setError(t("costPrice.subrecipes.duplicateNameError"));
            return;
        }

        setIsSaving(true);
        setError(null);
        const payloadIngredients = ingredients.map(toIngredientInput);
        try {
            const saved = isEditMode && subrecipeId
                ? await updateSubrecipe({
                    id: subrecipeId,
                    expectedUpdatedAt: draftVersion,
                    name: name.trim(),
                    description: description.trim() || undefined,
                    portions,
                    portionsUnit,
                    ingredients: payloadIngredients,
                })
                : await createSubrecipe({
                    restaurantId,
                    name: name.trim(),
                    description: description.trim() || undefined,
                    portions,
                    portionsUnit,
                    ingredients: payloadIngredients,
                }, practiceScope);
            setDraftVersion(recipeWriteVersion(saved));
            setSavedDraftSignature(draftSignature(saved));
            setIngredients(saved.ingredients);
            await reloadSubrecipes();
            onSaved?.(saved);
            // Hosts that keep the editor mounted (the setup stepper) drive navigation
            // from onSaved themselves — only close when we own the panel.
            if (closeOnSave) handleClose();
            else setIsSaving(false);
        } catch (err) {
            if (err instanceof StaleRecipeWriteError) {
                // Replace the entire stale draft and its token together, never just the token.
                try {
                    const latest = await fetchSubrecipeById(subrecipeId!);
                    setName(latest.name);
                    setDescription(latest.description ?? "");
                    setPortions(latest.portions);
                    setPortionsInputValue(latest.portions.toString());
                    setPortionsUnit(latest.portionsUnit);
                    setIngredients(latest.ingredients);
                    setDraftVersion(recipeWriteVersion(latest));
                    setSavedDraftSignature(draftSignature(latest));
                    setInlineQuantities({});
                    setInlineWaste({});
                } catch (refreshError) {
                    console.error('Failed to refresh changed subrecipe:', refreshError);
                }
                setError(err.message);
            } else if (err instanceof SubrecipeCycleApiError) {
                const childName = subrecipesById.get(err.child)?.name ?? err.child;
                setError(t("costPrice.subrecipes.cycleError", { child: childName, parent: name }));
            } else if (err instanceof SubrecipeDepthApiError) {
                setError(t("costPrice.subrecipes.depthError"));
            } else if (err instanceof SubrecipeDuplicateNameApiError) {
                // Server backstop for the race the client-side pre-check can't catch.
                setError(t("costPrice.subrecipes.duplicateNameError"));
            } else {
                console.error("Failed to save subrecipe:", err);
                // Prefer the server's message (parseWriteError surfaces it) over a
                // bare generic fallback so the user sees something actionable.
                const serverMessage = err instanceof Error ? err.message : null;
                setError(serverMessage || (isEditMode
                    ? t("costPrice.editSubrecipe.failedToUpdate")
                    : t("costPrice.subrecipeDetail.failedToCreate")));
            }
            setIsSaving(false);
        }
    }, [restaurantId, canSave, ingredients, draftVersion, isEditMode, subrecipeId, name, description, portions, portionsUnit, reloadSubrecipes, onSaved, handleClose, closeOnSave, subrecipesById, subrecipesList, t, practiceScope]);

    return {
        // Compare against the loaded draft, not a refreshed store row. Asking never saves edits.
        hasUnsavedChanges: !savedDraftSignature || savedDraftSignature !== draftSignature({ name, description, portions, portionsUnit, ingredients }),
        // Meta
        isEditMode,
        isLoading: isLoading || (sourceContext && sourceLoading),
        isSaving,
        error: sourceContext && sourceFailed ? t("costPrice.editSubrecipe.failedToUpdate") : error,
        sourceData: sourceContext ? scopedData ?? emptySourceData : undefined,
        reloadSubrecipes,
        restaurantId,
        subrecipeId,

        // Draft fields
        name, setName,
        description, setDescription,
        portions,
        portionsInputValue,
        handlePortionsChange,
        portionsUnit, setPortionsUnit,

        // Rows
        ingredients,
        enrichedNormalIngredients,
        nestedRows,

        // Inline edit state + handlers
        inlineQuantities,
        inlineWaste,
        confirmingDeleteId,
        handleQuantityChange,
        handleSaveEditQuantity,
        handleWasteChange,
        handleSaveWaste,
        handleDeleteRow,
        handleConfirmDeleteRow,
        handleNestedQuantityChange,

        // Picker
        excludedSubrecipeIds,

        // Cost
        totalCost,
        unitCost,

        // Save gating
        canSave,

        // UI state
        isPanelVisible,
        isMaximized, setIsMaximized,
        showAddIngredients, setShowAddIngredients,
        selectedNestedSubrecipeId, setSelectedNestedSubrecipeId,
        alternativesIngredientId, setAlternativesIngredientId,
        customLinkIngredientId, setCustomLinkIngredientId,
        handleApplyCustomLink,
        showDeleteModal, setShowDeleteModal,

        // Handlers
        handleAddSelection,
        handleSaveAlternatives,
        handleClose,
        handleSave,
        defaultWastePercentage,
    };
}
